import { userKey } from './storageHelper.js';
// src/utils/systemProfile.js
// ─────────────────────────────────────────────────────────────────────────────
// Central data access layer — reads the user's AI system profile from
// localStorage (onboarding) and makes it available to all tools.
//
// This solves the Data Silo problem: instead of each tool (DocumentGenerator,
// RiskCalculator, EUAIActClassifier, CrossFrameworkMapper) asking the user to
// re-type their company name and AI description, they call getSystemProfile()
// and get everything pre-filled from the onboarding data.
//
// When the enriched onboarding (Phase 1) adds structured AI system fields,
// all tools automatically get those richer fields with no code changes.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Read the full system profile from localStorage onboarding data.
 * Returns a normalized object all tools can consume.
 */
export function getSystemProfile() {
  try {
    const raw = localStorage.getItem(userKey('qais_onboarding'));
    if (!raw) return defaultProfile();
    const data = JSON.parse(raw);
    if (!data.completed) return defaultProfile();
    return buildProfile(data);
  } catch {
    return defaultProfile();
  }
}

/**
 * React hook — returns system profile and re-reads if onboarding changes.
 * Usage:  const profile = useSystemProfile();
 */
export function useSystemProfile() {
  // Simple read — onboarding rarely changes mid-session
  // Tools that need live updates can call getSystemProfile() directly
  return getSystemProfile();
}

/**
 * Build a normalized profile from raw onboarding data.
 * Handles both old format (aiSystems text) and new format (structured aiSystem object).
 */
function buildProfile(data) {
  // Support both old text format and new structured format
  const aiSystemRaw = data.aiSystems || '';
  const aiSystemStructured = data.aiSystemProfile || null;

  return {
    // ── Company ──────────────────────────────────────────────
    companyName:       data.companyName || '',
    industry:          data.industry || '',
    companySize:       data.companySize || '',
    deploymentRegions: data.deploymentRegions || '',

    // ── AI System (structured if available, text fallback) ────
    // These are the fields every tool needs for document generation
    systemName:        aiSystemStructured?.name        || extractSystemName(aiSystemRaw) || 'AI System',
    systemPurpose:     aiSystemStructured?.purpose     || aiSystemRaw || '',
    modelType:         aiSystemStructured?.modelType   || '',
    dataCategories:    aiSystemStructured?.dataCategories || [],
    affectedUsers:     aiSystemStructured?.affectedUsers || '',
    lifecycleStage:    aiSystemStructured?.lifecycleStage || 'deployed',
    businessOwner:     aiSystemStructured?.businessOwner || data.companyName || '',
    technicalOwner:    aiSystemStructured?.technicalOwner || '',
    deploymentDate:    aiSystemStructured?.deploymentDate || '',

    // ── Compliance Profile ────────────────────────────────────
    euAiRisk:          data.euAiRisk || '',
    primaryGoal:       data.primaryGoal || '',
    currentGovernance: data.currentGovernance || '',

    // ── Convenience fields for tool prompts ──────────────────
    // Pre-formatted strings tools can drop directly into AI prompts
    promptContext: buildPromptContext(data, aiSystemStructured, aiSystemRaw),
    hasStructuredProfile: !!aiSystemStructured,
  };
}

function defaultProfile() {
  return {
    companyName: '', industry: '', companySize: '', deploymentRegions: '',
    systemName: '', systemPurpose: '', modelType: '', dataCategories: [],
    affectedUsers: '', lifecycleStage: 'deployed', businessOwner: '',
    technicalOwner: '', deploymentDate: '',
    euAiRisk: '', primaryGoal: '', currentGovernance: '',
    promptContext: '', hasStructuredProfile: false,
  };
}

function extractSystemName(text) {
  // Try to extract a system name from free text like "a chatbot for customer service"
  if (!text) return '';
  const lower = text.toLowerCase();
  if (lower.includes('chatbot')) return 'AI Chatbot';
  if (lower.includes('classifier')) return 'AI Classifier';
  if (lower.includes('recommendation')) return 'Recommendation Engine';
  if (lower.includes('fraud')) return 'Fraud Detection System';
  if (lower.includes('hr') || lower.includes('recruit')) return 'HR AI System';
  if (lower.includes('credit') || lower.includes('loan')) return 'Credit Scoring System';
  return 'AI System';
}

function buildPromptContext(data, structured, rawText) {
  const lines = [];
  if (data.companyName)  lines.push(`Company: ${data.companyName}`);
  if (data.industry)     lines.push(`Industry: ${data.industry}`);
  if (data.companySize)  lines.push(`Company Size: ${data.companySize}`);

  if (structured) {
    lines.push(`AI System: ${structured.name || 'AI System'}`);
    lines.push(`Purpose: ${structured.purpose || ''}`);
    if (structured.modelType)    lines.push(`Model Type: ${structured.modelType}`);
    if (structured.dataCategories?.length) lines.push(`Data Categories: ${structured.dataCategories.join(', ')}`);
    if (structured.affectedUsers) lines.push(`Affected Users: ${structured.affectedUsers}`);
    if (structured.lifecycleStage) lines.push(`Lifecycle Stage: ${structured.lifecycleStage}`);
    if (structured.businessOwner) lines.push(`Business Owner: ${structured.businessOwner}`);
  } else if (rawText) {
    lines.push(`AI System Description: ${rawText}`);
  }

  if (data.euAiRisk)          lines.push(`EU AI Act Risk Level: ${data.euAiRisk}`);
  if (data.deploymentRegions) lines.push(`Deployment Region: ${data.deploymentRegions}`);
  if (data.primaryGoal)       lines.push(`Primary Compliance Goal: ${data.primaryGoal}`);
  if (data.currentGovernance) lines.push(`Current Governance Maturity: ${data.currentGovernance}`);

  return lines.join('\n');
}

/**
 * clearLockedFieldDependencies()
 * Call this whenever companyName or industry changes via Edit Profile.
 * Clears all downstream data that was calculated based on those locked fields.
 */
export function clearLockedFieldDependencies() {
  // Use userKey() so only the current user's data is cleared
  const keysToReset = [
    userKey('qais_risk_result'),
    userKey('qais_checklist_setup'),
    userKey('qais_checklist_progress'),
    userKey('qais_eu_classifier_result'),
    userKey('qais_action_states_minimal'),
    userKey('qais_action_states_limited'),
    userKey('qais_action_states_high'),
    userKey('qais_action_states_unacceptable'),
  ];
  keysToReset.forEach(key => {
    try { localStorage.removeItem(key); } catch {}
  });
}
